import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { useOutlet } from '../contexts/OutletContext';
import { useCurrency } from '../contexts/CurrencyContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Calendar } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Badge } from '../components/ui/badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { toast } from 'sonner';
import { format, subDays, startOfMonth } from 'date-fns';
import {
  BarChart3,
  DollarSign,
  TrendingUp,
  ShoppingCart,
  CreditCard,
  Users,
  CalendarIcon,
  Download,
  Loader2,
  FileSpreadsheet,
  FileText,
} from 'lucide-react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  LineChart,
  Line,
} from 'recharts';
import { ReportSummarySkeleton, ChartSkeleton } from '../components/ui/loading-skeletons';

const COLORS = ['#EA580C', '#15803D', '#0369A1', '#B45309', '#7C3AED'];

export default function Reports() {
  const { t } = useTranslation();
  const { api } = useAuth();
  const { currentOutlet } = useOutlet();
  const { formatCurrency, currencySettings } = useCurrency();
  const [dateRange, setDateRange] = useState({
    start: subDays(new Date(), 30),
    end: new Date(),
  });
  const [loading, setLoading] = useState(false);
  const [salesData, setSalesData] = useState(null);
  const [topItems, setTopItems] = useState([]);
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [cashierPerformance, setCashierPerformance] = useState([]);
  const [activeTab, setActiveTab] = useState('sales');

  const fetchReports = useCallback(async () => {
    if (!currentOutlet?.id) return;
    setLoading(true);
    try {
      const startDate = format(dateRange.start, 'yyyy-MM-dd');
      const endDate = format(dateRange.end, 'yyyy-MM-dd');
      const params = { outlet_id: currentOutlet.id, start_date: startDate, end_date: endDate };

      const [salesResp, topResp, paymentResp, cashierResp] = await Promise.all([
        api.get('/reports/sales', { params }),
        api.get('/reports/top-items', { params: { ...params, limit: 10 } }),
        api.get('/reports/payment-methods', { params }),
        api.get('/reports/cashier-performance', { params }),
      ]);

      setSalesData(salesResp.data);
      setTopItems(topResp.data);
      setPaymentMethods(paymentResp.data);
      setCashierPerformance(cashierResp.data);
    } catch (error) {
      toast.error('Failed to fetch reports');
    } finally {
      setLoading(false);
    }
  }, [api, currentOutlet?.id, dateRange]);

  useEffect(() => {
    fetchReports();
  }, [fetchReports]);

  const formatCurrencyPlain = (amount) => {
    // For export/print - uses settings but returns plain number with thousand separator
    const { thousand_separator = '.', decimal_places = 0, decimal_separator = ',' } = currencySettings || {};
    const rounded = Number((amount || 0).toFixed(decimal_places));
    const [intPart, decPart] = rounded.toFixed(decimal_places).split('.');
    const formattedInt = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, thousand_separator);
    if (decimal_places > 0 && decPart) {
      return formattedInt + decimal_separator + decPart;
    }
    return formattedInt;
  };

  const exportToCSV = () => {
    if (!salesData) {
      toast.error('No data to export');
      return;
    }

    const startDate = format(dateRange.start, 'yyyy-MM-dd');
    const endDate = format(dateRange.end, 'yyyy-MM-dd');
    
    let csvContent = '';
    
    // Sales Summary
    csvContent += 'DAPOER BENGAWAN - SALES REPORT\n';
    csvContent += `Outlet: ${currentOutlet.name}\n`;
    csvContent += `Period: ${startDate} to ${endDate}\n\n`;
    
    csvContent += 'SUMMARY\n';
    csvContent += `Total Revenue,${formatCurrencyPlain(salesData.total_sales)}\n`;
    csvContent += `Total Orders,${salesData.total_orders}\n`;
    csvContent += `Average Order Value,${formatCurrencyPlain(salesData.average_order_value)}\n\n`;
    
    // Daily Breakdown
    if (salesData.daily_breakdown?.length > 0) {
      csvContent += 'DAILY BREAKDOWN\n';
      csvContent += 'Date,Revenue,Orders\n';
      salesData.daily_breakdown.forEach(day => {
        csvContent += `${day.date},${formatCurrencyPlain(day.revenue)},${day.orders}\n`;
      });
      csvContent += '\n';
    }
    
    // Top Items
    if (topItems.length > 0) {
      csvContent += 'TOP SELLING ITEMS\n';
      csvContent += 'Item Name,Quantity Sold,Revenue\n';
      topItems.forEach(item => {
        csvContent += `${item.name},${item.quantity},${formatCurrencyPlain(item.revenue)}\n`;
      });
      csvContent += '\n';
    }
    
    // Payment Methods
    if (paymentMethods.length > 0) {
      csvContent += 'PAYMENT METHODS\n';
      csvContent += 'Method,Transactions,Total Amount\n';
      paymentMethods.forEach(method => {
        csvContent += `${method.method},${method.count},${formatCurrencyPlain(method.total)}\n`;
      });
      csvContent += '\n';
    }
    
    // Cashier Performance
    if (cashierPerformance.length > 0) {
      csvContent += 'CASHIER PERFORMANCE\n';
      csvContent += 'Cashier Name,Orders,Revenue\n';
      cashierPerformance.forEach(cashier => {
        csvContent += `${cashier.name},${cashier.orders},${formatCurrencyPlain(cashier.revenue)}\n`;
      });
    }
    
    // Create and download file
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `sales_report_${startDate}_to_${endDate}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    toast.success('Report exported to CSV!');
  };

  const exportToPDF = () => {
    if (!salesData) {
      toast.error('No data to export');
      return;
    }

    const startDate = format(dateRange.start, 'yyyy-MM-dd');
    const endDate = format(dateRange.end, 'yyyy-MM-dd');
    
    // Create printable HTML
    const printContent = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Sales Report - Dapoer Bengawan</title>
        <style>
          body { font-family: Arial, sans-serif; padding: 20px; color: #333; }
          h1 { color: #EA580C; border-bottom: 2px solid #EA580C; padding-bottom: 10px; }
          h2 { color: #444; margin-top: 30px; }
          .header { margin-bottom: 20px; }
          .summary { display: grid; grid-template-columns: repeat(3, 1fr); gap: 20px; margin: 20px 0; }
          .summary-card { background: #f5f5f5; padding: 15px; border-radius: 8px; text-align: center; }
          .summary-card .value { font-size: 24px; font-weight: bold; color: #EA580C; }
          .summary-card .label { color: #666; font-size: 14px; }
          table { width: 100%; border-collapse: collapse; margin: 20px 0; }
          th, td { border: 1px solid #ddd; padding: 10px; text-align: left; }
          th { background: #EA580C; color: white; }
          tr:nth-child(even) { background: #f9f9f9; }
          .footer { margin-top: 40px; text-align: center; color: #888; font-size: 12px; }
          @media print { body { padding: 0; } }
        </style>
      </head>
      <body>
        <div class="header">
          <h1>🍽️ Dapoer Bengawan - Sales Report</h1>
          <p><strong>Outlet:</strong> ${currentOutlet.name}</p>
          <p><strong>Period:</strong> ${startDate} to ${endDate}</p>
          <p><strong>Generated:</strong> ${format(new Date(), 'PPpp')}</p>
        </div>
        
        <h2>Summary</h2>
        <div class="summary">
          <div class="summary-card">
            <div class="value">${currencySettings?.currency_symbol || 'Rp'} ${formatCurrencyPlain(salesData.total_sales)}</div>
            <div class="label">Total Revenue</div>
          </div>
          <div class="summary-card">
            <div class="value">${salesData.total_orders}</div>
            <div class="label">Total Orders</div>
          </div>
          <div class="summary-card">
            <div class="value">${currencySettings?.currency_symbol || 'Rp'} ${formatCurrencyPlain(salesData.average_order_value)}</div>
            <div class="label">Average Order Value</div>
          </div>
        </div>
        
        ${topItems.length > 0 ? `
          <h2>Top Selling Items</h2>
          <table>
            <thead>
              <tr><th>#</th><th>Item Name</th><th>Qty Sold</th><th>Revenue</th></tr>
            </thead>
            <tbody>
              ${topItems.map((item, i) => `
                <tr>
                  <td>${i + 1}</td>
                  <td>${item.name}</td>
                  <td>${item.quantity}</td>
                  <td>${currencySettings?.currency_symbol || 'Rp'} ${formatCurrencyPlain(item.revenue)}</td>
                </tr>
              `).join('')}
            </tbody>
          </table>
        ` : ''}
        
        ${paymentMethods.length > 0 ? `
          <h2>Payment Methods</h2>
          <table>
            <thead>
              <tr><th>Method</th><th>Transactions</th><th>Total Amount</th></tr>
            </thead>
            <tbody>
              ${paymentMethods.map(method => `
                <tr>
                  <td>${method.method}</td>
                  <td>${method.count}</td>
                  <td>${currencySettings?.currency_symbol || 'Rp'} ${formatCurrencyPlain(method.total)}</td>
                </tr>
              `).join('')}
            </tbody>
          </table>
        ` : ''}
        
        ${cashierPerformance.length > 0 ? `
          <h2>Cashier Performance</h2>
          <table>
            <thead>
              <tr><th>#</th><th>Cashier</th><th>Orders</th><th>Revenue</th></tr>
            </thead>
            <tbody>
              ${cashierPerformance.map((cashier, i) => `
                <tr>
                  <td>${i + 1}</td>
                  <td>${cashier.name}</td>
                  <td>${cashier.orders}</td>
                  <td>${currencySettings?.currency_symbol || 'Rp'} ${formatCurrencyPlain(cashier.revenue)}</td>
                </tr>
              `).join('')}
            </tbody>
          </table>
        ` : ''}
        
        <div class="footer">
          <p>Dapoer Bengawan POS System</p>
        </div>
      </body>
      </html>
    `;
    
    const printWindow = window.open('', '_blank');
    printWindow.document.write(printContent);
    printWindow.document.close();
    printWindow.onload = () => {
      printWindow.print();
    };
    
    toast.success('Report ready for printing/PDF!');
  };

  const handleExport = (type) => {
    if (type === 'csv') {
      exportToCSV();
    } else if (type === 'pdf') {
      exportToPDF();
    }
  };

  const setPresetRange = (preset) => {
    const today = new Date();
    switch (preset) {
      case 'today':
        setDateRange({ start: today, end: today });
        break;
      case 'week':
        setDateRange({ start: subDays(today, 7), end: today });
        break;
      case 'month':
        setDateRange({ start: startOfMonth(today), end: today });
        break;
      case '30days':
        setDateRange({ start: subDays(today, 30), end: today });
        break;
      default:
        break;
    }
  };

  if (!currentOutlet) {
    return (
      <div className="flex h-full items-center justify-center p-6">
        <p className="text-muted-foreground">{t('reports.select_outlet_first')}</p>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6" data-testid="reports-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold">{t('reports.title')}</h1>
          <p className="text-muted-foreground">{t('reports.subtitle')}</p>
        </div>
        
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" size="sm" onClick={() => setPresetRange('today')}>{t('reports.today')}</Button>
          <Button variant="outline" size="sm" onClick={() => setPresetRange('week')}>{t('reports.days_7')}</Button>
          <Button variant="outline" size="sm" onClick={() => setPresetRange('month')}>{t('reports.this_month')}</Button>
          <Button variant="outline" size="sm" onClick={() => setPresetRange('30days')}>{t('reports.days_30')}</Button>
          
          <Popover>
            <PopoverTrigger asChild>
              <Button variant="outline" size="sm">
                <CalendarIcon className="h-4 w-4 mr-2" />
                {format(dateRange.start, 'MMM d')} - {format(dateRange.end, 'MMM d')}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="end">
              <Calendar
                mode="range"
                selected={{ from: dateRange.start, to: dateRange.end }}
                onSelect={(range) => {
                  if (range?.from && range?.to) {
                    setDateRange({ start: range.from, end: range.to });
                  }
                }}
                numberOfMonths={2}
              />
            </PopoverContent>
          </Popover>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm" data-testid="export-btn">
                <Download className="h-4 w-4 mr-2" />
                {t('reports.export')}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => handleExport('csv')} data-testid="export-csv">
                <FileSpreadsheet className="h-4 w-4 mr-2" />
                {t('reports.export_csv')}
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => handleExport('pdf')} data-testid="export-pdf">
                <FileText className="h-4 w-4 mr-2" />
                {t('reports.export_pdf')}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      {loading ? (
        <div className="space-y-6">
          <ReportSummarySkeleton />
          <ChartSkeleton height={300} />
        </div>
      ) : (
        <>
          {/* Summary Cards */}
          <div className="grid gap-4 grid-cols-2 lg:grid-cols-4">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground">{t('reports.total_revenue')}</CardTitle>
                <DollarSign className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold font-mono" data-testid="total-revenue">
                  {formatCurrency(salesData?.total_sales || 0)}
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground">{t('reports.total_orders')}</CardTitle>
                <ShoppingCart className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold font-mono" data-testid="total-orders">
                  {salesData?.total_orders || 0}
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground">{t('reports.avg_order_value')}</CardTitle>
                <TrendingUp className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold font-mono" data-testid="avg-order">
                  {formatCurrency(salesData?.average_order_value || 0)}
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground">{t('reports.active_cashiers')}</CardTitle>
                <Users className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold font-mono" data-testid="active-cashiers">
                  {cashierPerformance.length}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Charts */}
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList>
              <TabsTrigger value="sales">{t('reports.sales_trend')}</TabsTrigger>
              <TabsTrigger value="items">{t('reports.top_items')}</TabsTrigger>
              <TabsTrigger value="payments">{t('reports.payment_methods')}</TabsTrigger>
              <TabsTrigger value="cashiers">{t('reports.cashier_performance')}</TabsTrigger>
            </TabsList>

            <TabsContent value="sales" className="mt-4">
              <Card>
                <CardHeader>
                  <CardTitle>{t('reports.daily_sales_trend')}</CardTitle>
                </CardHeader>
                <CardContent>
                  {salesData?.daily_breakdown?.length > 0 ? (
                    <div className="h-80">
                      <ResponsiveContainer width="100%" height="100%">
                        <LineChart data={salesData.daily_breakdown}>
                          <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                          <XAxis dataKey="date" tick={{ fontSize: 12 }} className="text-muted-foreground" />
                          <YAxis tick={{ fontSize: 12 }} tickFormatter={(val) => `${(val / 1000000).toFixed(1)}M`} className="text-muted-foreground" />
                          <Tooltip
                            formatter={(value) => [formatCurrency(value), t('reports.total_revenue')]}
                            contentStyle={{ backgroundColor: 'hsl(var(--card))', border: '1px solid hsl(var(--border))' }}
                          />
                          <Line type="monotone" dataKey="revenue" stroke="#EA580C" strokeWidth={2} dot={{ fill: '#EA580C' }} />
                        </LineChart>
                      </ResponsiveContainer>
                    </div>
                  ) : (
                    <p className="text-center text-muted-foreground py-12">{t('reports.no_sales_data')}</p>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="items" className="mt-4">
              <Card>
                <CardHeader>
                  <CardTitle>{t('reports.top_selling_items')}</CardTitle>
                </CardHeader>
                <CardContent>
                  {topItems.length > 0 ? (
                    <div className="h-80">
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={topItems} layout="vertical">
                          <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                          <XAxis type="number" tick={{ fontSize: 12 }} />
                          <YAxis type="category" dataKey="name" tick={{ fontSize: 12 }} width={120} />
                          <Tooltip
                            formatter={(value, name) => [name === 'quantity' ? `${value} sold` : formatCurrency(value), name]}
                            contentStyle={{ backgroundColor: 'hsl(var(--card))', border: '1px solid hsl(var(--border))' }}
                          />
                          <Bar dataKey="quantity" fill="#EA580C" radius={[0, 4, 4, 0]} />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  ) : (
                    <p className="text-center text-muted-foreground py-12">{t('reports.no_item_data')}</p>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="payments" className="mt-4">
              <Card>
                <CardHeader>
                  <CardTitle>{t('reports.payment_method_dist')}</CardTitle>
                </CardHeader>
                <CardContent>
                  {paymentMethods.length > 0 ? (
                    <div className="flex items-center gap-8">
                      <div className="h-64 w-64">
                        <ResponsiveContainer width="100%" height="100%">
                          <PieChart>
                            <Pie
                              data={paymentMethods}
                              dataKey="total"
                              nameKey="method"
                              cx="50%"
                              cy="50%"
                              outerRadius={80}
                              label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                            >
                              {paymentMethods.map((_, index) => (
                                <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                              ))}
                            </Pie>
                            <Tooltip
                              formatter={(value) => formatCurrency(value)}
                              contentStyle={{ backgroundColor: 'hsl(var(--card))', border: '1px solid hsl(var(--border))' }}
                            />
                          </PieChart>
                        </ResponsiveContainer>
                      </div>
                      <div className="space-y-3">
                        {paymentMethods.map((method, index) => (
                          <div key={method.method} className="flex items-center justify-between gap-4">
                            <div className="flex items-center gap-2">
                              <div
                                className="w-3 h-3 rounded-full"
                                style={{ backgroundColor: COLORS[index % COLORS.length] }}
                              />
                              <span>{method.method}</span>
                            </div>
                            <div className="text-right">
                              <p className="font-mono font-medium">{formatCurrency(method.total)}</p>
                              <p className="text-xs text-muted-foreground">{method.count} {t('reports.transactions')}</p>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  ) : (
                    <p className="text-center text-muted-foreground py-12">{t('reports.no_payment_data')}</p>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="cashiers" className="mt-4">
              <Card>
                <CardHeader>
                  <CardTitle>{t('reports.cashier_performance')}</CardTitle>
                </CardHeader>
                <CardContent>
                  {cashierPerformance.length > 0 ? (
                    <div className="space-y-4">
                      {cashierPerformance.map((cashier, index) => (
                        <div
                          key={cashier.cashier_id}
                          className="flex items-center justify-between p-4 rounded-md bg-muted/50"
                        >
                          <div className="flex items-center gap-4">
                            <span className="flex h-10 w-10 items-center justify-center rounded-full bg-primary/10 text-primary font-bold">
                              {index + 1}
                            </span>
                            <div>
                              <p className="font-medium">{cashier.name}</p>
                              <p className="text-sm text-muted-foreground">{cashier.orders} {t('reports.orders')}</p>
                            </div>
                          </div>
                          <div className="text-right">
                            <p className="font-mono text-lg font-semibold">{formatCurrency(cashier.revenue)}</p>
                            <p className="text-xs text-muted-foreground">
                              {t('reports.avg')}: {formatCurrency(cashier.revenue / cashier.orders)}
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-center text-muted-foreground py-12">{t('reports.no_cashier_data')}</p>
                  )}
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </>
      )}
    </div>
  );
}
